import React from "react";
import PetCarouselSection from "../PetCarouselSection";
import VideoCarousel from "../VideoCarousel";
import { HappyParentsSection } from "../PetSectionComponents.tsx/ForeverHomesSection";
import ReviewSection from "../ProductDetailComponent.tsx/ReviewSection";
import PetCardCustomerComponent from "../PetSectionComponents.tsx/PetCardCustomerComponent";
import CommonButton from "../../CommonButton";
import { Search, MessageSquare, CheckCircle2 } from "lucide-react";
import { ProductItem } from "../../../utils/Customer/CustomerTypes";
import Carousel from "../Carousel";

interface PetSectionRendererProps {
  section: any;
  data: any;
  handlers: any;
}

export const PetSectionRenderer: React.FC<PetSectionRendererProps> = ({
  section,
  data,
  handlers,
}) => {
  const sectionType = section?.sectionType ?? section?.sectionName;

  switch (sectionType) {
    // =========================================================
    // MAIN CAROUSEL
    // =========================================================

    case "MainCarousel": {
      if (!section?.data?.length) {
        return null;
      }

      return (
        <div className="w-full overflow-hidden rounded-xl">
          <Carousel
            items={section.data}
            onItemClick={(item) => handlers.onBannerClick?.(item)}
          />
        </div>
      );
    }

    // =========================================================
    // DOGS
    // =========================================================

   case "PetCarouselDogs": {
  if (!Array.isArray(section?.data) || section.data.length === 0) {
    return null;
  }

  const mappedData = section.data.map((pet: any) => ({
    id: pet.Id ?? pet.id,
    PetCode: pet.PetCode ?? pet.petCode,
    name: pet.Name ?? pet.name,
    breed: pet.Breed ?? pet.breed,
    category: pet.Category ?? pet.category,
    gender: pet.Gender ?? pet.gender,
    price: pet.Price ?? pet.price,
    salePrice: pet.SalePrice ?? pet.salePrice,
    imageUrl: pet.ImageUrl ?? pet.imageUrl,
    tag: pet.Tag ?? pet.tag,
    isFeatured: pet.IsFeatured ?? pet.isFeatured ?? false,
    petType:pet.PetType ?? pet.petType,
    petStatus:
      pet.PetStatus ??
      pet.petStatus ??
      1,
  }));

  return (
    <PetCarouselSection
      title={section.sectionName || "Buy Furry Friend"}
      products={mappedData}
      onAddToCartClick={handlers.handleClickDog}
      onCategoryClick={handlers.onCategoryClick}
      onViewAllClick={handlers.petsViewAll}
    />
  );
}

    // =========================================================
    // VIDEO CAROUSEL
    // =========================================================
case "VideoCarouselPets": {
  const rawList = section?.data || [];

  const mappedVideos = rawList.map((video: any) => ({
    PetId: video.PetId || video.petId || video.id,
    PetName: video.PetName || video.petName || video.name,
    videoUrl: video.VideoUrl || video.videoUrl,
    imageUrl: video.ProductImage || video.ThumbnailUrl || video.productImage || video.thumbnailUrl || video.imageUrl,
    ProductImage: video.ProductImage || video.ThumbnailUrl || video.productImage || video.thumbnailUrl,
    ThumbnailUrl: video.ThumbnailUrl || video.ProductImage || video.thumbnailUrl,
    Price: video.Price ?? video.price,
    SalePrice: video.SalePrice ?? video.salePrice,
    BookPrice: video.BookPrice ?? video.bookPrice,
    sku: video.PetCode || video.petCode || "",
    category: "Pets",
    Breed: video.Breed || video.breed || "",
    Tag: video.Tag || video.tag || "",
  }));

  if (!mappedVideos.length) {
    return null;
  }

  return (
    <VideoCarousel
      items={mappedVideos}
      handleViewAll={handlers.onViewAllClick}
      layout="overlay"
      title={
        <>
          Find Your <span className="text-mediumGreen">Perfect Pet</span>
        </>
      }
      description="Healthy, playful, and loving companions waiting to become a part of your family."
      badgeText="Sponsored"
      showallDetails={false}
      handleProductPage={handlers.onVideoClick}
    />
  );
}

    // =========================================================
    // CATS
    // =========================================================

case "PetCarouselCats": {
  if (!Array.isArray(section?.data) || section.data.length === 0) {
    return null;
  }

  const mappedData = section.data.map((pet: any) => ({
    id: pet.Id ?? pet.id,
    PetCode: pet.PetCode ?? pet.petCode,
    name: pet.Name ?? pet.name,
    breed: pet.Breed ?? pet.breed,
    category: pet.Category ?? pet.category,
    gender: pet.Gender ?? pet.gender,
    price: pet.Price ?? pet.price,
    salePrice: pet.SalePrice ?? pet.salePrice,
    imageUrl: pet.ImageUrl ?? pet.imageUrl,
    tag: pet.Tag ?? pet.tag,
    isFeatured: pet.IsFeatured ?? pet.isFeatured ?? false,
     petType:pet.PetType ?? pet.petType,
    petStatus:
      pet.PetStatus ??
      pet.petStatus ??
      1,
  }));

  return (
    <PetCarouselSection
      title={section.sectionName || "Find Your Purrfect Companion"}
      products={mappedData}
      onAddToCartClick={handlers.handleClickDog}
      onCategoryClick={handlers.onCategoryClick}
      onViewAllClick={handlers.petsViewAll}
    />
  );
}

    // =========================================================
    // FIXED SECTIONS
    // =========================================================

    case "HappyParentsSection":
      return <HappyParentsSection />;

    case "ReviewSection":
      return <ReviewSection />;

    // =========================================================
    // ADOPT
    // =========================================================

    case "AdoptSection":
      return (
        <div className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-iconColor">
                Adopt a Furry Friend in UAE
              </h2>

              <p className="text-gray-500">
                Every pet deserves a loving home. Start your journey across the
                Emirates.
              </p>
            </div>

            {/* <CommonButton text="View All 150+ Pets" variant="outline" /> */}
          </div>

          <PetCardCustomerComponent
            handleBooknow={handlers.handleClickDog}
            data={section?.data?.length ? section.data : data?.MixBreed}
          />
        </div>
      );

    // =========================================================
    // CONSULTATION
    // =========================================================

    case "ConsultationBanner": {
      const bannerInfo = section?.data?.length
        ? section.data[0]
        : {
            title: "Struggling to find the right pet?",
            subTitle:
              "Book a free 15-minute consultation with our Dubai-based behaviorists to find a breed that fits your lifestyle.",
            imageUrl:
              "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=500",
          };

      return (
        <div className="group relative border-2 border-accent/20 rounded-[2.5rem] overflow-hidden flex flex-col md:flex-row shadow-xl hover:shadow-2xl transition-all duration-500 bg-[#eef7eb]">
          <div className="bg-[#d9edd2] p-6 lg:p-8 w-full md:w-[25%] lg:w-[20%] flex flex-col justify-between items-start shrink-0 relative z-20">
            <span className="inline-block px-3 py-1 bg-white/70 backdrop-blur-md rounded-full text-[10px] font-extrabold uppercase tracking-widest text-logoHeading mb-3 border border-white/50">
              Free Advice
            </span>

            <div>
              <h2 className="text-xl lg:text-2xl font-black text-logoHeading leading-tight tracking-tight">
                {bannerInfo.title}
              </h2>

              <p className="text-gray-700 mt-3 mb-6 font-medium text-xs lg:text-sm leading-relaxed">
                {bannerInfo.subTitle}
              </p>
            </div>

            <div className="w-full">
              <CommonButton
                text="Book Free Call"
                className="w-full bg-logoHeading text-white border-none rounded-xl px-4 h-11 text-xs font-bold shadow-md hover:bg-black hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
              />
            </div>
          </div>

          <div className="relative min-h-[280px] md:min-h-[380px] w-full md:w-[75%] lg:w-[80%] overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#d9edd2] to-transparent z-10 hidden md:block" />

            <img
              src={bannerInfo.imageUrl}
              className="absolute inset-0 w-full h-full object-cover object-center transform group-hover:scale-105 transition-transform duration-700 ease-out"
              alt="consultation background"
            />

            <div className="absolute inset-0 bg-black/5 pointer-events-none" />
          </div>
        </div>
      );
    }

    // =========================================================
    // HOW ADOPTION WORKS
    // =========================================================

    case "HowAdoptionWorks":
      return (
        <div className="bg-logoHeading rounded-[2.5rem] p-14 text-white relative overflow-hidden shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative z-10">
            {[
              {
                icon: <Search size={28} />,
                t: "Choose Your Companion",
                d: "Find a happy, healthy pet that's perfect for your family.",
              },
              {
                icon: <MessageSquare size={28} />,
                t: "A Little Hello",
                d: "Learn about your future companion and reserve them with ease.",
              },
              {
                icon: <CheckCircle2 size={28} />,
                t: "Home at Last",
                d: "Complete your order and welcome endless cuddles and tail wags.",
              },
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 mx-auto bg-white/10 rounded-xl flex items-center justify-center mb-4 text-white">
                  {step.icon}
                </div>

                <h4 className="text-xl font-bold mb-2">{step.t}</h4>

                <p className="text-white/60 text-xs">{step.d}</p>
              </div>
            ))}
          </div>
        </div>
      );

    // =========================================================
    // NEWSLETTER
    // =========================================================

    case "NewsletterBox":
      return (
        <div className="bg-softCream border rounded-[2.5rem] p-10 text-center">
          <h2 className="text-2xl font-black text-logoHeading mb-2">
            Join the UAE Pet Family 🐾
          </h2>

          <div className="flex flex-col md:flex-row gap-4 max-w-xl mx-auto mt-6">
            <input
              type="email"
              placeholder="Your Email Address"
              className="flex-1 px-6 py-3 rounded-xl border bg-white"
            />

            <CommonButton
              text="Join the Pack"
              className="bg-mainBrandColor text-white rounded-xl px-8"
            />
          </div>
        </div>
      );

    default:;

      return null;
  }
};
